import React, { useState, useRef, useEffect, useCallback } from "react";
import { exportToExcel } from "../util/exportToExcel";
import "./CalcFare.css";
import * as xlsx from "xlsx";
const CalcFare = () => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const fileInput1Ref = useRef(null);
  const fileInput2Ref = useRef(null);
  const [ctcData, setCTCData] = useState([]);
  const [systemData, setSystemData] = useState([]);
  const [totalFare, setTotalFare] = useState(0);
  const [memberRides, setMemberRides] = useState([]);
  const [cancelledRides, setCancelledRides] = useState(0);
  const [finishedRides, setFinishedRides] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);

  // Get previous month as default
  const getPreviousMonth = () => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return prevMonth.getMonth(); // 0-11
  };

  const [selectedMonth, setSelectedMonth] = useState(getPreviousMonth());
  const handleFileChange = (e, setFile) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setFile(file);
      }
    }
  };

  const validateFile = (file) => {
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    const validExtensions = [".xls", ".xlsx", ".csv"];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (
      !validExtensions.includes(fileExtension) &&
      !validTypes.includes(file.type)
    ) {
      setErrorMsg("Please upload a valid Excel file (.xls, .xlsx, or .csv)");
      setShowError(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (ctcData.length > 0 && systemData.length > 0) {
      filterUniqueData();
    }
  }, [ctcData, systemData]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleSubmit = () => {
    if (!file1 || !file2) {
      setErrorMsg("Please upload both files before proceeding.");
      setShowError(true);
      return;
    }

    const readFile = (file) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = xlsx.read(data, { type: "array" });
          resolve(workbook);
        };
        reader.readAsArrayBuffer(file);
      });

    Promise.all([readFile(file1), readFile(file2)]).then(
      ([workbook1, workbook2]) => {
        const sheet1 = workbook1.Sheets[workbook1.SheetNames[0]];
        const sheet2 = workbook2.Sheets[workbook2.SheetNames[0]];

        const data1 = xlsx.utils.sheet_to_json(sheet1);
        const data2 = xlsx.utils.sheet_to_json(sheet2);

        // console.log("File 1:", data1);
        // console.log("File 2:", data2);

        return Promise.all([cleanCTCData(data1), cleanSystemData(data2)]);
      }
    );
  };

  const cleanCTCData = (data1) => {
    return new Promise((resolve) => {
      const ctcBotData = data1;
      //get desired month data
      const ctcMonthData = ctcBotData.filter((item) => {
        if (!item.DOS) return false;
        const dosDate = parseDOS(item.DOS);
        const dosMonth = dosDate.getMonth(); // 0-11

        return dosMonth === selectedMonth;
      });
      //remove leg from CTC id
      const ctcCleanedData = ctcMonthData.map((item) => {
        if (!item["Reservation No."]) return item;
        const cleanRes = item["Reservation No."].replace(/-[^-]*$/, "");

        return {
          ...item,
          "Reservation No.": cleanRes,
        };
      });
      setCTCData(ctcCleanedData);
      // console.log("ctcCleanedData", ctcCleanedData);
      resolve(ctcCleanedData);
    });
  };

  const cleanSystemData = (data2) => {
    return new Promise((resolve) => {
      let systemData = data2;
      //keep only CTC data only
      systemData = systemData.filter(
        (item) =>
          item["Funding Source"]?.trim().toLowerCase() === "call the car"
      );
      //remove leg from CTC id
      const systemCleanedData = systemData.map((item) => {
        if (!item["Trip ID"]) return item;

        const cleanRes = item["Trip ID"].split("-").slice(0, -1).join("-");

        return {
          ...item,
          "Trip ID": cleanRes,
          "Original Trip ID": item["Trip ID"],
        };
      });

      setSystemData(systemCleanedData);
      // console.log("systemCleanedData", systemCleanedData);
      resolve(systemCleanedData);
    });
  };

  const filterUniqueData = useCallback(() => {
    const normalize = (str) =>
      (str || "").trim().replace(/\s+/g, " ").toLowerCase();

    //1: Extract CTC Reservation IDs
    const ctcReservationIds = ctcData.map((item) =>
      normalize(item["Reservation No."])
    );
    //2: Find matching System Data records
    const matchedData = systemData.filter((item) => {
      return ctcReservationIds.includes(normalize(item["Trip ID"]));
    });
    // console.log("matchedData", matchedData);

    //3: Process each member only once
    const processedMembers = new Set();
    let allMemberRides = [];

    matchedData.forEach((match) => {
      const memberName = normalize(match["Member Name"]);

      // Skip if processed
      if (processedMembers.has(memberName)) return;

      processedMembers.add(memberName);

      const ridesForThisMember = systemData.filter(
        (s) => normalize(s["Member Name"]) === memberName
      );

      allMemberRides.push(...ridesForThisMember);
    });

    //add leg back to trip ids
    allMemberRides = allMemberRides.map((ride) => ({
      ...ride,
      "Trip ID": ride["Original Trip ID"] || ride["Trip ID"],
    }));

    //5: Calculate ride statistics
    const finishedRidesData = allMemberRides.filter(
      (item) => normalize(item.Status) === "finished"
    );
    const cancelledRidesData = allMemberRides.filter(
      (item) => normalize(item.Status) === "cancelled"
    );

    const totalFinishedFare = finishedRidesData.reduce(
      (sum, item) => sum + Number(item.Fare || 0),
      0
    );

    setMemberRides(allMemberRides);
    setFinishedRides(finishedRidesData.length);
    setCancelledRides(cancelledRidesData.length);
    setTotalFare(totalFinishedFare);
    setShowPopup(true);
  }, [ctcData, systemData]);

  const parseDOS = (dosString) => {
    // Example: "10/07/2025 0705"
    const [datePart] = dosString.split(" "); // ["10/07/2025", "0705"]
    const [month, day, year] = datePart.split("/"); // ["10", "07", "2025"]

    // return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
    return new Date(`${year}-${month}-${day}`);
  };

  const handleExport = () => {
    let summaryData = [
      {
        "Total CTC Bot Trips": ctcData.length,
        "Total CTC Trips in System": systemData.length,
        "Total Standing Order Trips": memberRides.length,
        "Finished Rides": finishedRides,
        "Cancelled Rides": cancelledRides,
        "Total Fare ($)": totalFare,
      },
    ];

    let keysToRemove = [
      "Funding Source",
      "Phone Number 1",
      "Phone Number 2",
      "Mileage",
      "Schedule Time",
      "Actual Pick-up Time",
      "Appt. Time",
      "Actual Drop-Off Time",
      "Level Of Service",
      "Driver Name",
      "Notes/Dispatch Notes",
      "On Scene At",
      "Member Onboard At",
      "Finished At",
      "Cancelled At",
      "Cancelled Reason",
      "Confirmed",
      "No Answer",
      "Bad Number",
      "Voicemail",
      "Original Trip ID",
    ];
    let tripDetails = memberRides.map((ride) => {
      const newRide = { ...ride };
      keysToRemove.forEach((key) => delete newRide[key]);
      return newRide;
    });

    exportToExcel(
      [
        {
          sheetName: "Summary",
          data: summaryData,
        },
        {
          sheetName: "Trip Details",
          data: tripDetails,
        },
      ],
      "CTC Trips Details"
    );
  };

  return (
    <div className="calc-fare-container">
      <div className="header-section">
        <h1 className="main-title">Fare Metrics Calculator</h1>
        <p className="subtitle">
          Upload your Excel files to calculate and compare fare metrics
        </p>
      </div>

      <div className="upload-grid">
        {/* File Upload Zone 1 */}
        <div className="upload-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <svg
                className="file-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="card-title">CTC Bot File</h2>
          </div>

          <div
            className={`drop-zone ${file1 ? "has-file" : ""}`}
            onClick={() => fileInput1Ref.current?.click()}
          >
            {!file1 ? (
              <>
                <svg
                  className="upload-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="drop-text">Click to select file</p>
                <p className="file-types">Supported formats: XLS, XLSX, CSV</p>
              </>
            ) : (
              <div className="file-preview">
                <div className="file-info">
                  <svg
                    className="file-success-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="file-details">
                    <p className="file-name">{file1.name}</p>
                    <p className="file-size">{formatFileSize(file1.size)}</p>
                    <p className="file-hint">Click to replace</p>
                  </div>
                </div>
              </div>
            )}
            <input
              ref={fileInput1Ref}
              type="file"
              accept=".csv, .xls, .xlsx"
              onChange={(e) => handleFileChange(e, setFile1)}
              style={{ display: "none" }}
            />
          </div>
        </div>

        {/* File Upload Zone 2 */}
        <div className="upload-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <svg
                className="file-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="card-title">System File</h2>
          </div>

          <div
            className={`drop-zone ${file2 ? "has-file" : ""}`}
            onClick={() => fileInput2Ref.current?.click()}
          >
            {!file2 ? (
              <>
                <svg
                  className="upload-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="drop-text">Click to select file</p>
                <p className="file-types">Supported formats: XLS, XLSX, CSV</p>
              </>
            ) : (
              <div className="file-preview">
                <div className="file-info">
                  <svg
                    className="file-success-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="file-details">
                    <p className="file-name">{file2.name}</p>
                    <p className="file-size">{formatFileSize(file2.size)}</p>
                    <p className="file-hint">Click to replace</p>
                  </div>
                </div>
              </div>
            )}
            <input
              ref={fileInput2Ref}
              type="file"
              accept=".csv, .xls, .xlsx"
              onChange={(e) => handleFileChange(e, setFile2)}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>

      <div className="action-section">
        <div className="month-selector">
          <label htmlFor="month-select" className="month-label">
            <svg
              className="calendar-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Select Month
          </label>
          <select
            id="month-select"
            className="month-dropdown"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            <option value={0}>January</option>
            <option value={1}>February</option>
            <option value={2}>March</option>
            <option value={3}>April</option>
            <option value={4}>May</option>
            <option value={5}>June</option>
            <option value={6}>July</option>
            <option value={7}>August</option>
            <option value={8}>September</option>
            <option value={9}>October</option>
            <option value={10}>November</option>
            <option value={11}>December</option>
          </select>
        </div>

        <button
          className={`submit-button ${file1 && file2 ? "active" : "disabled"}`}
          onClick={handleSubmit}
          disabled={!file1 || !file2}
        >
          <svg
            className="button-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          Calculate Fare Metrics
        </button>
      </div>

      {/* Results Popup */}
      {showPopup && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div
            className="popup-content popup-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header">
              <h2 className="popup-title">CTC Trips Report</h2>
              <div className="header-actions">
                <button
                  className="export-button"
                  onClick={() => handleExport()}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export
                </button>
                <button
                  className="popup-close"
                  onClick={() => setShowPopup(false)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="popup-body">
              <div className="result-tiles-grid">
                {/* Tile 1: Total CTC Rides */}
                <div className="result-tile">
                  <div className="tile-icon tile-icon-purple">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                  <div className="tile-content">
                    <p className="tile-value">{ctcData.length}</p>
                    <p className="tile-label">Total Bot CTC Trips</p>
                  </div>
                </div>

                {/* Tile 2: CTC in System */}
                <div className="result-tile">
                  <div className="tile-icon tile-icon-teal">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <div className="tile-content">
                    <p className="tile-value">{systemData.length}</p>
                    <p className="tile-label">Total CTC Trips in System</p>
                  </div>
                </div>

                {/* Tile 3: Total Trips */}
                <div className="result-tile">
                  <div className="tile-icon tile-icon-blue">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div className="tile-content">
                    <p className="tile-value">{memberRides.length}</p>
                    <p className="tile-label">Total Standing Order Trips</p>
                  </div>
                </div>

                {/* Tile 4: Finished Trips */}
                <div className="result-tile">
                  <div className="tile-icon tile-icon-green">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="tile-content">
                    <p className="tile-value">{finishedRides}</p>
                    <p className="tile-label">Finished Trips</p>
                  </div>
                </div>

                {/* Tile 5: Cancelled Rides */}
                <div className="result-tile">
                  <div className="tile-icon tile-icon-red">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <div className="tile-content">
                    <p className="tile-value">{cancelledRides}</p>
                    <p className="tile-label">Cancelled Trips</p>
                  </div>
                </div>

                {/* Tile 6: Total Fare */}
                <div className="result-tile">
                  <div className="tile-icon tile-icon-gold">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="tile-content">
                    <p className="tile-value">${totalFare.toFixed(2)}</p>
                    <p className="tile-label">Total Fare</p>
                  </div>
                </div>
              </div>

              {/* Detail Table */}
              {memberRides.length > 0 ? (
                <div className="table-container">
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th>CTC ID</th>
                        <th>Member Name</th>
                        <th>Pickup Address</th>
                        <th>Drop Off Address</th>
                        {/* <th>Pick-up</th> */}
                        {/* <th>Drop-off</th> */}
                        <th>Status</th>
                        <th>Fare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberRides.map((ride, index) => (
                        <tr key={index}>
                          <td>{ride["Trip ID"]}</td>
                          <td>{ride["Member Name"]}</td>
                          <td>{ride["Pick-up Address"]}</td>
                          <td>{ride["Drop-Off Address"]}</td>
                          {/* <td>{ride["Actual Pick-up Time"]}</td> */}
                          {/* <td>{ride["Actual Drop-Off Time"]}</td> */}
                          <td>
                            <span
                              className={`status-badge ${
                                (ride.Status || "").toLowerCase() === "finished"
                                  ? "status-finished"
                                  : (ride.Status || "").toLowerCase() ===
                                    "cancelled"
                                  ? "status-cancelled"
                                  : ""
                              }`}
                            >
                              {ride.Status}
                            </span>
                          </td>
                          <td>${Number(ride.Fare || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-placeholder">
                  <svg
                    className="no-data-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="no-data-text">No ride data found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showError && (
        <div className="popup-overlay" onClick={() => setShowError(false)}>
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "400px" }}
          >
            <div className="popup-header error-header">
              <h2 className="popup-title">Error</h2>
              <button
                className="popup-close"
                onClick={() => setShowError(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="popup-body" style={{ textAlign: "center" }}>
              <div
                className="tile-icon tile-icon-red"
                style={{
                  margin: "0 auto",
                  width: "60px",
                  height: "60px",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  style={{ width: "32px", height: "32px" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <p style={{ fontSize: "1.1rem", color: "#2d3748" }}>{errorMsg}</p>
              <button
                className="submit-button error-button"
                onClick={() => setShowError(false)}
                style={{ marginTop: "10px", width: "100%", padding: "10px" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalcFare;
